import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { MdFlag } from 'react-icons/md';
import Tooltip from '@srm/ui/tooltip';
import { IOfferCrit, ITableDemandItem } from '@models/winnerSelection/ITableDemand';
import { IMatrixCell, IMatrixField, IMatrixLot, IMatrixParticipant, IWinnerMatrix } from '../WinnerSelection2.types';
import type { PositionCriterionHandler } from '../WinnerSelection2.types';
import { groupCriteria, IMatrixIndex, IVisibleLot } from '../utils/matrixView';
import CounterpartyMarker from './CounterpartyMarker';
import CriteriaSection from './CriteriaSection';
import LotRow from './LotRow';
import MatrixState from './MatrixState';
import { CellIntent } from './OfferCell';
import PositionRow from './PositionRow';

interface IProps {
    matrix: IWinnerMatrix;
    index: IMatrixIndex;
    lots: IVisibleLot[];
    participants: IMatrixParticipant[];
    collapsed: ReadonlySet<string>;
    busy: boolean;
    metric: IMatrixField | null;
    nextMetricTitle: string | null;
    onNextMetric: () => void;
    onPositionCriterion: PositionCriterionHandler;
    onClearSearch: () => void;
    onToggleLot: (lotKey: string) => void;
    onCellIntent: (intent: CellIntent, cell: IMatrixCell, trigger: HTMLElement) => void;
    onDecideLot: (lot: IMatrixLot, participant: IMatrixParticipant, trigger: HTMLElement) => void;
    onCriterion: (
        item: ITableDemandItem,
        participant: IMatrixParticipant,
        value: IOfferCrit,
        trigger: HTMLElement,
    ) => void;
}

const idOf = (prefix: string, key: string) => `ws2-${prefix}-${key.replace(/[^\w-]/g, '_')}`;
const criterionRowId = (critId: string) => idOf('criterion', critId);

/**
 * Одна таблица на позиции и критерии: столбцы участников общие для обеих
 * секций, горизонтальный scroll один, оси закреплены через sticky.
 */
const MatrixGrid = forwardRef<HTMLDivElement, IProps>(
    (
        {
            matrix,
            index,
            lots,
            participants,
            collapsed,
            busy,
            metric,
            nextMetricTitle,
            onNextMetric,
            onPositionCriterion,
            onClearSearch,
            onToggleLot,
            onCellIntent,
            onDecideLot,
            onCriterion,
        },
        ref,
    ) => {
        const participantIds = participants.map((participant) => idOf('participant', participant.key));
        const criteriaGroups =
            lots.length > 0 ? groupCriteria(matrix.criteria.items, matrix.criteria.settings?.categorySortOrder) : [];
        const columns = participants.length + 1;
        const blackListHint = matrix.blackListHint ?? 'Контрагент в чёрном списке';

        return (
            <div
                ref={ref}
                className={clsx('ws2-viewport', lots.length === 0 && 'ws2-viewport--no-results')}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Scrollable region must support keyboard scrolling.
                tabIndex={0}
                role="region"
                aria-label="Матрица предложений: прокрутка по горизонтали и вертикали"
            >
                <table
                    className="ws2-matrix"
                    aria-label="Лоты, позиции и критерии по заявкам участников"
                    style={{ minWidth: `calc(var(--ws2-axis) + ${participants.length} * var(--ws2-col-min))` }}
                >
                    <colgroup>
                        <col className="ws2-matrix__axis-col" />
                        {participants.map((participant) => (
                            <col key={participant.key} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col">
                                <div className="ws2-matrix__corner-title">Лоты и позиции</div>
                                <div className="ws2-matrix__corner-hint">Потребность и выбранный объём</div>
                            </th>
                            {participants.map((participant, i) => (
                                <th scope="col" id={participantIds[i]} key={participant.key}>
                                    <div className="ws2-participant__title">
                                        <div className="ws2-participant__name">{participant.name}</div>
                                        {participant.isInBlackList && (
                                            <div className="ws2-participant__badges">
                                                <Tooltip
                                                    openDelay={100}
                                                    background="black"
                                                    position="bottom"
                                                    label={blackListHint}
                                                >
                                                    <span
                                                        className="ws2-participant__black-list"
                                                        role="img"
                                                        aria-label={blackListHint}
                                                    >
                                                        <MdFlag size="13px" aria-hidden />
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                    <div className="ws2-participant__meta">
                                        <span>
                                            {participant.number ? `Заявка № ${participant.number}` : 'Заявка'}
                                            {participant.type ? ` · ${participant.type}` : ''}
                                        </span>
                                        {participant.marker && <CounterpartyMarker marker={participant.marker} />}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {lots.length === 0 && (
                            <tr>
                                <td colSpan={columns}>
                                    <MatrixState kind="search" onAction={onClearSearch} />
                                </td>
                            </tr>
                        )}
                        {lots.map(({ lot, allPositions, positions }) => {
                            const lotRowId = idOf('lot', lot.key);
                            const isCollapsed = collapsed.has(lot.key);
                            return (
                                <React.Fragment key={lot.key}>
                                    <LotRow
                                        lot={lot}
                                        rowId={lotRowId}
                                        participants={participants}
                                        participantIds={participantIds}
                                        positionsCount={allPositions.length}
                                        collapsed={isCollapsed}
                                        priceVisible={matrix.priceVisible}
                                        canEdit={matrix.canEdit}
                                        busy={busy}
                                        onToggle={onToggleLot}
                                        onDecideLot={onDecideLot}
                                    />
                                    {!isCollapsed &&
                                        positions.map((position) => (
                                            <PositionRow
                                                key={position.key}
                                                position={position}
                                                rowId={idOf('position', position.key)}
                                                index={index}
                                                participants={participants}
                                                participantIds={participantIds}
                                                showComplianceComment={matrix.compliance.showCommentInline}
                                                priceMode={matrix.priceMode}
                                                priceVisible={matrix.priceVisible}
                                                metric={metric}
                                                nextMetricTitle={nextMetricTitle}
                                                onNextMetric={onNextMetric}
                                                onPositionCriterion={onPositionCriterion}
                                                busy={busy}
                                                onCellIntent={onCellIntent}
                                            />
                                        ))}
                                </React.Fragment>
                            );
                        })}

                        <CriteriaSection
                            groups={criteriaGroups}
                            participants={participants}
                            participantIds={participantIds}
                            busy={busy}
                            rowIdOf={criterionRowId}
                            onCriterion={onCriterion}
                        />
                    </tbody>
                </table>
            </div>
        );
    },
);

MatrixGrid.displayName = 'MatrixGrid';

export default MatrixGrid;
